import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MapPin, Search } from "lucide-react";
import {
  PLACES_ROUTES,
  PLACE_QUERY_MIN,
  placeDetailsResponseSchema,
  placeSuggestionsResponseSchema,
  type PlaceSuggestion,
  type ShippingAddress,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { cn } from "@/shared/lib/cn";

const NO_PREFIX = { baseUrl: "" } as const;

/**
 * ═══════════════════════════════════════════════════════════════════
 * Buscador de direcciones con Google Places.
 *
 * ── La clave NO está aquí ────────────────────────────────────────────
 * Este componente habla con `/api/places`, no con Google: la clave vive
 * en el servidor (ver `contracts/places.ts`). Aquí no hay script externo
 * que cargar ni `VITE_…` que filtrar en el bundle.
 *
 * ── Y si no hay clave, desaparece ────────────────────────────────────
 * El servidor responde `not_configured` y el buscador se esconde entero:
 * los campos de abajo se escriben a mano, que es lo que se hacía hasta
 * hoy. Nadie ve un buscador que no busca.
 *
 * ── El token de sesión no es un capricho ─────────────────────────────
 * Google factura el autocompletado por sesión si se manda el mismo
 * `sessionToken` en las pulsaciones y en el detalle final; sin él, cada
 * tecla es una búsqueda facturable. Se renueva al elegir una dirección,
 * que es donde la sesión termina.
 * ═══════════════════════════════════════════════════════════════════
 */
export function PlaceSearch({
  onPick,
  label,
  onUnavailable,
}: {
  onPick: (
    address: Partial<ShippingAddress> & { placeId?: string; country?: string },
  ) => void;
  label?: string;
  /** Se llama si el servidor no tiene clave: quien lo pinta abre el formulario. */
  onUnavailable?: () => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [available, setAvailable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const session = useRef(crypto.randomUUID());

  useEffect(() => {
    const term = query.trim();
    if (term.length < PLACE_QUERY_MIN) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setBusy(true);
      try {
        const data = await authFetch(
          `${PLACES_ROUTES.suggest}?q=${encodeURIComponent(term)}&session=${session.current}`,
          placeSuggestionsResponseSchema,
          NO_PREFIX,
        );
        if (cancelled) return;
        if (data.status === "not_configured") {
          setAvailable(false);
          setSuggestions([]);
          onUnavailable?.();
          return;
        }
        setSuggestions(data.suggestions);
        setOpen(data.suggestions.length > 0);
        setActive(-1);
      } catch {
        // Buscar es una comodidad: si falla, quedan los campos de abajo.
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, onUnavailable]);

  async function elegir(suggestion: PlaceSuggestion) {
    setOpen(false);
    setQuery("");
    setSuggestions([]);
    try {
      const data = await authFetch(
        `${PLACES_ROUTES.details(suggestion.placeId)}?session=${session.current}`,
        placeDetailsResponseSchema,
        NO_PREFIX,
      );
      /*
       * El país viaja aparte de la dirección (ver `contracts/places.ts`)
       * y se pasa tal cual: la sede lo usa para bajar su selector sola
       * cuando la dirección elegida es de Estados Unidos, y el checkout
       * —que no tiene ese campo— simplemente lo ignora.
       */
      onPick({ ...data.address, ...(data.country ? { country: data.country } : {}), placeId: suggestion.placeId });
    } catch {
      // Sin detalle, al menos el texto elegido sirve de calle.
      onPick({ street: `${suggestion.primary}, ${suggestion.secondary}`.trim() });
    } finally {
      // La sesión de Places termina al elegir: la siguiente búsqueda es otra.
      session.current = crypto.randomUUID();
    }
  }

  if (!available) return null;

  return (
    <div className="relative">
      <label className="grid gap-1.5">
        <span className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {label ?? t("Buscar mi dirección")}
        </span>
        <span className="relative flex items-center">
          <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-ds-ink-4" aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setOpen(suggestions.length > 0)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, suggestions.length - 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
              } else if (event.key === "Enter" && active >= 0) {
                event.preventDefault();
                void elegir(suggestions[active]!);
              } else if (event.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder={t("Escribe calle y número…")}
            className="w-full rounded-2xl border border-ds-border bg-white py-3 pl-10 pr-10 text-[14px] text-ds-ink outline-none transition focus:border-ds-violet-pop focus:ring-2 focus:ring-ds-violet-pop/20"
          />
          {busy && (
            <Loader2 className="absolute right-3.5 h-4 w-4 animate-spin text-ds-ink-4" aria-hidden="true" />
          )}
        </span>
      </label>

      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-2xl border border-ds-border bg-white shadow-ds-float"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.placeId} role="option" aria-selected={index === active}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void elegir(suggestion)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex w-full items-start gap-2.5 px-4 py-2.5 text-left transition",
                  index === active ? "bg-ds-lavender-bg" : "hover:bg-ds-cream-2/60",
                )}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ds-violet-pop" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block truncate text-[13.5px] font-medium text-ds-ink">
                    {suggestion.primary}
                  </span>
                  <span className="block truncate text-[12px] text-ds-ink-3">
                    {suggestion.secondary}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
